import { useMemo, useSyncExternalStore } from 'react';
import type { Editor } from '@tiptap/core';
import type { Node as PMNode } from '@tiptap/pm/model';
import { diffBounds, hasNodeOrMarkInRange } from '@modules/documents/tiptap/plugins/shared';

import {
  elementList,
  isSwapping,
  subscribeElements,
  subscribeSwapState,
  wholeDoc,
} from '@modules/documents/largeDocument';
import { NEW_COMMENT_ID, SELECTION_MARK_NAME, SELECTION_NODE_NAME } from '../constants';

const collectOrder = (doc: PMNode): ReadonlyMap<string, number> => {
  const order = new Map<string, number>();
  const add = (id: string | null) => {
    if (id && id !== NEW_COMMENT_ID && !order.has(id)) order.set(id, order.size);
  };
  doc.descendants((node) => {
    if (node.type.name === SELECTION_NODE_NAME) add(node.attrs.commentId);
    node.marks.forEach((mark) => {
      if (mark.type.name === SELECTION_MARK_NAME) add(mark.attrs.commentId);
    });
  });
  return order;
};

/** Only mounted with the comments panel. The editor/model own content; this is a display projection. */
const useCommentOccurrenceOrder = (editor: Editor | undefined, isContentReady = false) => {
  const store = useMemo(() => {
    let order: ReadonlyMap<string, number> = new Map();
    let previousDoc: PMNode | undefined;
    let previousElements: ReturnType<typeof elementList>;
    let previousSwapping: boolean | undefined;

    return {
      getSnapshot: () => {
        if (!editor || !isContentReady) return order;
        const doc = editor.state.doc;
        const elements = elementList();
        const swapping = isSwapping();
        const oldDoc = previousDoc;
        const modelChanged = elements !== previousElements || swapping !== previousSwapping;
        if (doc === oldDoc && !modelChanged) return order;
        previousDoc = doc;
        previousElements = elements;
        previousSwapping = swapping;
        // Compare final documents so appended transactions are included. Model changes still need a full read.
        if (oldDoc && !modelChanged) {
          const diff = diffBounds({ doc: oldDoc }, { doc });
          if (
            !diff ||
            (!hasNodeOrMarkInRange(oldDoc, diff.start, diff.oldEnd, SELECTION_NODE_NAME, SELECTION_MARK_NAME) &&
              !hasNodeOrMarkInRange(doc, diff.start, diff.newEnd, SELECTION_NODE_NAME, SELECTION_MARK_NAME))
          )
            return order;
        }
        const next = collectOrder(wholeDoc(editor, { livePart: doc }) ?? doc);
        if (next.size !== order.size || [...next].some(([id, rank]) => order.get(id) !== rank)) order = next;
        return order;
      },
      subscribe: (notify: () => void) => {
        if (!editor || !isContentReady) return () => {};
        // Read the final state, including changes made by appended transactions.
        editor.on('transaction', notify);
        const unsubscribeElements = subscribeElements(notify);
        const unsubscribeSwap = subscribeSwapState(notify);
        return () => {
          editor.off('transaction', notify);
          unsubscribeElements();
          unsubscribeSwap();
        };
      },
    };
  }, [editor, isContentReady]);

  return useSyncExternalStore(store.subscribe, store.getSnapshot);
};

export default useCommentOccurrenceOrder;
